/**
 * @author michalstolarski
 * @since 2021-12-03
 */

import React from "react";
import styled, { css } from "styled-components";

import { NAVIGATION_HEADER_SPACE_KEEPER_HEIGHT } from "@config/consts";

import { rem, transition } from "@ui/helpers";
import { BottomSheet } from "@ui/Atoms/BottomSheet";
import {
  SummaryTotal,
  Props as SummaryTotalProps,
} from "@ui/Atoms/SummaryTotal";
import { SummaryDescription } from "@ui/Atoms/SummaryDescription";

interface Props
  extends Omit<
    SummaryTotalProps,
    "variant" | "testId" | "testIds" | "className"
  > {
  testId?: string;
  testIds?: {
    price?: string;
    tooltip?: string;
    caption?: string;
    description?: string;
  };
  className?: string;
  description?: string;
  children: React.ReactElement;
  isHighlighted?: boolean;
  isPriceNotFixed?: boolean;
}

const SummaryComponent = (props: Props): React.ReactElement => {
  const $total = React.useRef<HTMLDivElement | null>(null);
  const $children = React.useRef<HTMLDivElement | null>(null);
  const [totalHeight, setTotalHeight] = React.useState(0);
  const [childrenHeight, setChildrenHeight] = React.useState(0);
  const [isOpen, setIsOpen] = React.useState(false);

  const totalResizeObserver = new ResizeObserver(entries => {
    if (entries.length !== 1) return;

    setTotalHeight((entries[0].target as HTMLDivElement).offsetHeight);
  });
  const childrenResizeObserver = new ResizeObserver(entries => {
    if (entries.length !== 1) return;

    setChildrenHeight((entries[0].target as HTMLDivElement).offsetHeight);
  });

  React.useEffect(() => {
    if (!$total.current) return;
    if (!$children.current) return;

    totalResizeObserver.observe($total.current);
    childrenResizeObserver.observe($children.current);

    setTotalHeight($total.current.offsetHeight);
    setChildrenHeight($children.current.offsetHeight);
  }, [$total, $children]);

  return (
    <React.Fragment>
      <BottomSheetStyled
        testId={props.testId}
        className={props.className}
        totalHeight={totalHeight}
        childrenHeight={childrenHeight}
        setToggleState={setIsOpen}
        isOpen={isOpen}
        hideCurtain
      >
        <TotalBar
          ref={$total}
          childrenHeight={childrenHeight}
          showContent={isOpen}
        >
          <Total
            testIds={{
              price: props.testIds?.price,
              tooltip: props.testIds?.tooltip,
              caption: props.testIds?.caption,
            }}
            variant="mobile"
            price={props.price}
            currency={props.currency}
            overridePrice={props.overridePrice}
            caption={props.caption}
            tooltip={props.tooltip}
            translations={props.translations}
            isLoading={props.isLoading}
            isBlue={props.isHighlighted}
          />
        </TotalBar>

        <Wrapper showContent={isOpen}>
          <ChildrenSection ref={$children} showContent={isOpen}>
            {props.children}
          </ChildrenSection>

          <div>
            <TotalSpaceKeeper height={totalHeight} />

            {!!props.description && (
              <Description
                testId={props.testIds?.description}
                content={props.description}
                showContent={isOpen}
              />
            )}
          </div>
        </Wrapper>
      </BottomSheetStyled>

      <SpaceKeeper height={totalHeight} />
    </React.Fragment>
  );
};

export { SummaryComponent };

const BottomSheetStyled = styled(BottomSheet)<{
  totalHeight: number;
  childrenHeight: number;
  isOpen: boolean;
}>`
  --total-height: ${({ totalHeight }) => rem(totalHeight)};
  --children-height: ${({ childrenHeight }) => rem(childrenHeight)};
  --sheet-padding-vertical: ${({ theme }) => theme.margins.base_x3};
  --sheet-padding-horizontal: ${({ theme }) => theme.margins.base_x2};
  --summary-transition: ${transition("transform")};

  max-height: calc(
    100% - ${rem(NAVIGATION_HEADER_SPACE_KEEPER_HEIGHT)} -
      ${props => props.theme.margins.base}
  );
  padding: var(--sheet-padding-vertical) var(--sheet-padding-horizontal);
  transform: translate3d(0, 0, 0);
  transition: var(--summary-transition);
  overflow: hidden;

  ${({ isOpen }) =>
    !isOpen &&
    css`
      transform: translate3d(
        0,
        calc(100% - var(--total-height) - (var(--sheet-padding-vertical) * 2)),
        0
      );
    `}

  touch-action: none;
`;

const Wrapper = styled.div<{ showContent: boolean }>`
  transform: translate3d(0, 0, 0);
  transition: var(--summary-transition);
  touch-action: none;

  ${({ showContent }) =>
    !showContent &&
    css`
      transform: translate3d(0, calc(var(--children-height) * -1), 0);
    `}
`;

const TotalBar = styled.div<{
  childrenHeight: number;
  showContent: boolean;
}>`
  position: absolute;
  top: var(--sheet-padding-vertical);
  left: 0;
  right: 0;
  padding: 0 var(--sheet-padding-horizontal);
  background-color: var(--bottom-sheet-background);
  transition: var(--summary-transition);
  z-index: 1;

  ${({ showContent, childrenHeight }) =>
    showContent &&
    css`
      transform: translate3d(0, ${rem(childrenHeight)}, 0);
    `}
`;

const Total = styled(SummaryTotal)<{
  isBlue?: boolean;
}>`
  ${props =>
    props.isBlue &&
    css`
      color: ${props.theme.palette.blue500};
    `}
`;

const TotalSpaceKeeper = styled.div<{ height: number }>`
  height: ${props => rem(props.height)};
`;

const Description = styled(SummaryDescription)<{ showContent: boolean }>`
  transition: ${transition("opacity")};

  ${({ showContent }) =>
    !showContent &&
    css`
      opacity: 0;
    `}
`;

const ChildrenSection = styled.div<{ showContent: boolean }>`
  position: relative;
  padding: 0 0 ${props => props.theme.margins.base};
  transition: ${transition("opacity")};
  overflow: hidden auto;
  overscroll-behavior: none;

  ${({ showContent }) =>
    !showContent &&
    css`
      opacity: 0;
    `}
`;

const SpaceKeeper = styled.div<{ height: number }>`
  // same like BottomSheetStyled component
  --sheet-padding-vertical: ${({ theme }) => theme.margins.base_x3};

  height: calc(
    ${props => rem(props.height)} + (var(--sheet-padding-vertical) * 2)
  );
  order: 9999;
`;
